import React, { useContext } from "react";

import DynamicTableVirtual from "../components/table-virtual";
import Topbar from "../components/topbar";
import { useListSearch, ListTitle } from "../components/ui/list-search";
import AddTaxPopup from "../components/popup/add-tax";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import { useToast } from "../providers/toaster-provider";
import { useMediaQuery, useTheme, Button, Box, Chip, Tooltip, Fab } from "@mui/material";
import { Menu, Plus } from "lucide-react";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import type { Column } from '../components/table-virtual';
import AddBtn from "../components/ui/add-btn";
import taxImage from '../files/images/tax.png';

import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import { useUser } from "../providers/user-provider";
import { usePermissions } from "../providers/permissions-provider";
import { useFeatures } from "../providers/features-provider";
import { set } from "date-fns";

type TaxDefinition = {
    id: string;
    name: string;
    amount: string;
    enabled: boolean;
    showInTransactions: boolean;
    isSystemGst: boolean;
    // How many transactions this tax is applied to, and the most that can
    // still be edited/deleted here (above that, it's a support request).
    transactionCount?: number;
    changeLimit?: number;
}

const TaxDefinitions: React.FC = () => {
    const { showSuccess, showError, showWarning } = useToast();
    // A tax applied to too many transactions can only be changed by support
    // (enforced server-side too) - say so instead of opening the form.
    const isLockedForChanges = (row: any) => !!row.changeLimit && (row.transactionCount ?? 0) >= row.changeLimit;
    const warnLocked = (row: any) => showWarning(
        `This tax is applied to ${Number(row.transactionCount).toLocaleString()} transactions, so it can only be changed by our support team. Please contact support@fineants.com.`
    );
    const { user } = useUser();
    const { can } = usePermissions();
    const { reload: reloadFeatures } = useFeatures();
    const canCreate = can('tax_definitions', 'create');
    const canUpdate = can('tax_definitions', 'update');
    const canDelete = can('tax_definitions', 'delete');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const [data, setData] = React.useState<TaxDefinition[]>([]);
    const list = useListSearch(data);
    const [selectedTax, setSelectedTax] = React.useState<TaxDefinition | null>(null);
    const [openModal, setOpenModal] = React.useState<string>('');
    
    const [fetch, setFetch] = useApi();
    const [create, setCreate] = useApi();
    const [update, setUpdate] = useApi();
    const [toggle, setToggle] = useApi();
    const [toggleShowInTransactions, setToggleShowInTransactions] = useApi();
    const [remove, setRemove] = useApi();

    React.useEffect(() => {
        document.title = "FineAnts | Tax Definitions";
    }, []);

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.taxDefinitions.fetchByEntity, [{ param: 'entityId', value: user?.entityId }, { param: 'showDisabled', value: 'true' }, { param: 'includeUsage', value: 'true' }]);
        setFetch({ url, method });
        // toggle/toggleShowInTransactions must trigger a refetch too - disabling
        // a tax definition also forces showInTransactions off server-side, and
        // the grid's local row state won't pick that up without a fresh fetch.
    }, [create, update, remove, toggle, toggleShowInTransactions, user]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && !isLoading) {
            setData(data.map((tax: any) => ({
                id: tax.id,
                name: tax.name,
                rate: tax.rate.toFixed(2) + '%',
                enabled: tax.isEnabled,
                showInTransactions: tax.showInTransactions,
                isSystemGst: !!tax.isSystemGst,
                transactionCount: tax.transactionCount ?? 0,
                changeLimit: tax.changeLimit,
            })));
        }
        if (error) {
            showError("Failed to fetch tax definitions.");
        }
    }, [fetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (data && !isLoading) {
            setOpenModal('');
        }
    }, [create]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (data && !isLoading) {
            showSuccess("Tax Definition created successfully");
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : "Failed to create Tax Definition");
        }
    }, [create]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (data && !isLoading) {
            showSuccess("Tax Definition updated successfully");
            setOpenModal('');
        }
        if (error) {
            showError(update.errorData?.error || (error ? error.toString() : "Failed to update Tax Definition"));
        }
    }, [update]);

    React.useEffect(() => {
        const { data, isLoading, error } = toggle;
        if (data && !isLoading) {
            showSuccess("Tax Definition updated successfully");
            // Toggling GST's Enabled state can flip the GST menu item's
            // visibility - refresh the sidebar's feature flags immediately
            // instead of requiring a full page reload.
            reloadFeatures();
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to update Tax Definition");
        }
    }, [toggle]);

    React.useEffect(() => {
        const { data, isLoading, error } = toggleShowInTransactions;
        if (data && !isLoading) {
            showSuccess("Tax Definition updated successfully");
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to update Tax Definition");
        }
    }, [toggleShowInTransactions]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (data && !isLoading) {
            showSuccess("Tax Definition deleted successfully");
            setOpenModal('');
        }
        if (error) {
            showError(remove.errorData?.error || (error ? error.toString() : "Failed to delete Tax Definition"));
        }
    }, [remove]);

    const handleCreate = (taxData: any) => {
        const { url, method } = buildApiRequestConfig(routes.taxDefinitions.create);
        setCreate({ url, method, body: { ...taxData, entityId: user?.entityId } });
    };

    const handleUpdate = (taxData: any) => {
        const { url, method } = buildApiRequestConfig(routes.taxDefinitions.update, [{ param: 'id', value: selectedTax?.id }]);
        setUpdate({ url, method, body: taxData });
    };

    const handleDelete = () => {
        const { url, method } = buildApiRequestConfig(routes.taxDefinitions.delete, [{ param: 'id', value: selectedTax?.id }]);
        setRemove({ url, method });
    };

    const columns: Column[] = [
        {
            id: 'name',
            label: 'Name',
            size: 150,
            type: 'custom',
            mobilePrimary: true,
            mobileOrder: 1,
            mobileWidth: 140,
            mobileHideLabel: true,
            customElement: (value, row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {value}
                    {row.isSystemGst && (
                        <Tooltip title="Auto-generated for Australian entities. Its rate can't be changed, but you can turn it off with the Enabled toggle.">
                            <Chip label="System" size="small" sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', fontSize: 10, height: 18 }} />
                        </Tooltip>
                    )}
                </Box>
            ),
        },
        { id: 'rate', label: 'Amount', size: 150, mobilePrimary: true, mobileOrder: 2, mobileWidth: 80 },
        {
            id: 'enabled',
            label: 'Enabled',
            size: 150,
            type: 'toggle',
            mobilePrimary: true,
            mobileOrder: 3,
            mobileWidth: 70,
            onToggle: (row, value) => {
                const { id } = row;
                if (!id) return;

                // Shown straight away - the refetch that follows runs in the
                // background (see isLoading below), so the page doesn't reload.
                // Turning a tax off also turns its Show in Transactions off
                // (server-side), so that's mirrored too.
                setData(prev => prev.map(t => (t.id === id ? { ...t, enabled: value, ...(value ? {} : { showInTransactions: false }) } : t)));
                const { url, method } = buildApiRequestConfig(routes.taxDefinitions.toggleEnabled, [{ param: 'id', value: id }]);
                setToggle({ url, method });
            }},
        {
            id: 'showInTransactions',
            label: 'Show in Transactions',
            size: 150,
            type: 'toggle',
            onToggle: (row, value) => {
                const { id } = row;
                if (!id) return;

                setData(prev => prev.map(t => (t.id === id ? { ...t, showInTransactions: value } : t)));
                const { url, method } = buildApiRequestConfig(routes.taxDefinitions.toggleShowInTransactions, [{ param: 'id', value: id }]);
                setToggleShowInTransactions({ url, method });
            }},
        {
            id: 'actions',
            label: 'Actions',
            size: 50,
            type: 'actions',
            onDelete: (row) => {
                if (isLockedForChanges(row)) return warnLocked(row);
                setOpenModal('delete'); setSelectedTax(row as TaxDefinition);
            },
            onEdit: (row) => {
                if (isLockedForChanges(row)) return warnLocked(row);
                setOpenModal('edit'); setSelectedTax(row as TaxDefinition);
            },
            canEdit: (row) => !row.isSystemGst,
            canDelete: (row) => !row.isSystemGst,
        },
    ];
    return (
        <div style={{ width: '100%' }}>
            <AddTaxPopup
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => setOpenModal('')}
                onConfirm={(newTax) => {
                    if (openModal === 'edit') {
                        handleUpdate(newTax);
                    } else {
                        handleCreate(newTax);
                    }
                }}
                taken={data.map(tax => tax.name)}
                isEdit={{ id: selectedTax?.id ? Number(selectedTax.id) : null, state: openModal === 'edit' }}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Tax Definition?"
                description="Are you sure you want to delete this tax definition? This action cannot be undone."
                image={{ src: taxImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    if (!selectedTax) return;
                    handleDelete();
                }}
            />
            <Topbar
                title={<ListTitle title="Tax Definitions" list={list} />}
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                    {!isMobile && (
                        <AddBtn disabled={!canCreate} onClick={() => {setOpenModal('add'); setSelectedTax(null);}} label="Add Tax" />
                    )}
                </Box>
            </Topbar>
            <div style={{ padding: '10px' }}>
                <DynamicTableVirtual
                    columns={columns}
                    data={list.filtered}
                    // Only the first load shows the loading state - the refetch after
                    // a toggle/edit refreshes the rows in place instead of blanking
                    // the table, which looked like a page reload.
                    isLoading={fetch.isLoading && !data.length}
                    scrollMode="page"
                    canEditRows={canUpdate}
                    canDeleteRows={canDelete}
                />
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && canCreate && (
                <Fab
                    onClick={() => {setOpenModal('add'); setSelectedTax(null);}}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        background: 'var(--color-primary)',
                        color: '#fff',
                        '&:hover': { background: 'var(--color-primary-hover)' },
                        zIndex: 999,
                    }}
                >
                    <Plus size={24} />
                </Fab>
            )}
        </div>
    );
};

export default TaxDefinitions;